// react
import { useContext } from 'react';

// types
import { Utilization, UtilizationCurrency, UtilizationTransaction } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../../pages/Project/Project.context';

// mui
import { TableCell, TableRow, Typography } from '@mui/material';

// ui
import { ChipCoverage } from '../../../ui/ChipCoverage/ChipCoverage';
import { ChipUtilization } from '../../../ui/ChipUtilization/ChipUtilization';
import { Currency } from '../../../ui/Currency/Currency';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { formatPercentage } from '../../../../utils/format';

export interface TableBasicUtilizationCurrenciesProps extends Omit<TableProps, 'headers'> {
	data: UtilizationTransaction[];
	utilization: Utilization;
}

export const TableBasicUtilizationCurrencies = ({
	data = [],
	utilization,
	...props
}: TableBasicUtilizationCurrenciesProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);

	const currencyReporting = project?.currencies?.find((c) => c.type === 'REPORTING');

	return (
		<Table
			borderRadius={2}
			color="grey"
			data={data}
			hasBorder={true}
			headers={[
				{
					label: t('currency'),
					render: (currency: string) => {
						return <Currency currency={currency} />;
					},
					value: 'currency',
				},
				{
					label: t('expensed'),
					render: (
						expensed: UtilizationCurrency['out'],
						header: TableHeader,
						row: UtilizationCurrency
					) => {
						return formatCurrency({
							currency: row.currency,
							notation: 'compact',
							value: expensed.total,
						});
					},
					value: 'out',
				},
				{
					label: t('disbursed'),
					render: (
						disbursed: UtilizationCurrency['in'],
						header: TableHeader,
						row: UtilizationCurrency
					) => {
						return formatCurrency({
							currency: row.currency,
							notation: 'compact',
							value: disbursed.total,
						});
					},
					value: 'in',
				},
				{
					label: t('utilization'),
					render: (value: number) => {
						return typeof value === 'number' ? formatPercentage(value) : t('na');
					},
					value: 'value',
				},
				{
					label: t('coverage'),
					render: (coverage: number | null) => {
						return typeof coverage === 'number' ? formatPercentage(coverage) : t('na');
					},
					TooltipProps: {
						title: t('transactionsWithCurrency', { currency: currencyReporting?.currency }),
					},
					value: 'coverage',
				},
			]}
			{...props}>
			<TableRow
				sx={(theme) => {
					return {
						borderTopColor: theme.palette.divider,
						borderTopStyle: 'solid',
						borderTopWidth: 1,
						backgroundColor: theme.palette.grey[50],
					};
				}}>
				<TableCell colSpan={3}>
					<Typography fontWeight={600} variant="caption">
						{t('average')}
					</Typography>
				</TableCell>
				<TableCell>
					<ChipUtilization utilization={utilization} />
				</TableCell>
				<TableCell>
					<ChipCoverage utilization={utilization} />
				</TableCell>
			</TableRow>
		</Table>
	);
};
